import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function FeaturedDecisions() {
  const {
    t
  } = useLanguage();
  const decisions = t('featured.cards') as any[];
  const allLabel = t('featured.all') as string;
  // Category filter chips derived from the cards, with an "All" option first.
  const categories = useMemo(() => {
    const unique = Array.from(new Set(decisions.map(d => d.category)));
    return [allLabel, ...unique];
  }, [decisions, allLabel]);
  const [activeCategory, setActiveCategory] = useState(allLabel);
  // Keep the selection valid when the language (and label set) changes.
  const currentCategory = categories.includes(activeCategory) ? activeCategory : allLabel;
  const visible = currentCategory === allLabel ? decisions : decisions.filter(d => d.category === currentCategory);
  return <section className="py-20 md:py-32 relative z-10" data-id="element-52" data-mp="1grbd3t9p9ylm">
      <div className="max-w-7xl mx-auto px-5 sm:px-6" data-id="element-53" data-mp="1gq00r526viiw">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 md:mb-12 gap-6" data-id="element-54" data-mp="1bshzm0uey7lg">
          <div data-id="element-55" data-mp="17187mtle6s6e">
            <h2 className="text-[40px] leading-[1.05] md:text-4xl font-display font-medium mb-4" data-id="element-56" data-mp="170q4re485fpi">
              {t('featured.title')}
            </h2>
            <p className="text-white/50 text-[18px] leading-[1.5] md:text-lg max-w-xl font-light" data-id="element-57" data-mp="120c4wjcs1c2k">
              {t('featured.subtitle')}
            </p>
          </div>
          <button className="text-sm font-medium text-white/70 hover:text-white transition-colors flex items-center gap-2 w-fit focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded-md" data-id="element-58" data-mp="t0xfln8cj7jn">
            {t('featured.viewAll')} <ArrowUpRight className="w-4 h-4" data-id="element-59" data-mp="j5x9f3poc1jh" />
          </button>
        </div>

        {/* Category filter — sliding pill (layoutId) */}
        <div role="tablist" aria-label={t('featured.title') as string} className="flex flex-wrap gap-2 mb-10 md:mb-14" data-id="element-60" data-mp="2dhbxlg31dsx4">
          {categories.map(cat => {
          const isActive = cat === currentCategory;
          return <button key={cat} role="tab" aria-selected={isActive} onClick={() => setActiveCategory(cat)} className={`relative px-4 py-2 rounded-full text-[13px] font-medium transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${isActive ? 'text-white' : 'text-white/50 hover:text-white/80'}`} data-id="element-61" data-mp="1u8ddzuxeq37">
                {isActive && <motion.span layoutId="featuredCategoryPill" className="absolute inset-0 rounded-full bg-white/10 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]" transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} data-id="element-62" data-mp="lkagi1e482gi" />}
                <span className="relative z-10" data-id="element-63" data-mp="32slsq570iv5b">{cat}</span>
              </button>;
        })}
        </div>

        <div className="flex overflow-x-auto snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 pb-8 md:pb-0 -mx-5 px-5 sm:mx-0 sm:px-0 hide-scrollbar" data-id="element-64" data-mp="3cooz5n8wdfy2">
          <AnimatePresence mode="popLayout" data-id="element-65" data-mp="37nj59m3i3mmy">
            {visible.map(item => <motion.div key={item.title} layout initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 10
          }} transition={{
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="group flex flex-col h-full glass-panel p-7 md:p-8 rounded-[2rem] border border-white/5 md:hover:border-white/10 md:hover:bg-surface/60 transition-all duration-500 cursor-pointer relative overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.2)] md:hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)] md:hover:-translate-y-1 min-w-[85vw] max-w-[340px] md:min-w-0 md:max-w-none snap-center shrink-0" data-id="element-66" data-mp="2k0avorz1hrf4">
                <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] rounded-[2rem] pointer-events-none" data-id="element-67" data-mp="1i9jnn1efs1wv" />
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-68" data-mp="1n7l9tv75shck" />
                <div className="flex items-center justify-between mb-8 relative z-10" data-id="element-69" data-mp="1w9ksst1qhvi5">
                  <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-white/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)]" data-id="element-70" data-mp="3t24dl6twte52">
                    {item.category}
                  </div>
                  <div className="text-xs text-white/40 font-mono uppercase tracking-wider" data-id="element-71" data-mp="3qira5idnvgy7">
                    {item.readTime}
                  </div>
                </div>

                <div className="flex-1 flex flex-col" data-id="element-72" data-mp="3gqlm2jqj5joa">
                  <h3 className="text-2xl font-display font-medium text-white/90 group-hover:text-white transition-colors leading-snug mb-6" data-id="element-73" data-mp="3gp0oel8x5km7">
                    {item.title}
                  </h3>

                  <div className="mt-auto pt-6 border-t border-white/5 space-y-4" data-id="element-74" data-mp="d8afnylyctgt">
                    <div className="flex items-center justify-between" data-id="element-75" data-mp="8cd0vwqc7v65">
                      <span className="text-sm text-white/50 font-light" data-id="element-76" data-mp="3b5otlh5w9to">
                        {t('featured.labels.confidence')}
                      </span>
                      <span className="text-sm font-medium text-accent" data-id="element-77" data-mp="2o2fzrsjtvmmc">
                        {item.confidence}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1" data-id="element-78" data-mp="2k376spwm3sru">
                      <span className="text-xs text-white/40 font-mono uppercase tracking-wider" data-id="element-79" data-mp="2ljb8ftrvoqpm">
                        {t('featured.labels.recommendation')}
                      </span>
                      <span className="text-sm text-white/80 font-medium" data-id="element-80" data-mp="2bkvd1eltivab">
                        {item.recommendation}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}